import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectSpread from "@babel/runtime/helpers/objectSpread2";
import _typeof from "@babel/runtime/helpers/typeof";
import _size from "lodash/size";
import _intersection from "lodash/intersection";
import _every from "lodash/every";
import _some from "lodash/some";
import _camelCase from "lodash/camelCase";
import _get from "lodash/get";
import _set from "lodash/set";
import _isNil from "lodash/isNil";
import _reduce from "lodash/reduce";
import "core-js/modules/es7.object.values.js";
import "core-js/modules/es7.array.includes.js";
import "core-js/modules/es6.number.constructor.js";
import "core-js/modules/es6.array.filter.js";
import "core-js/modules/es6.array.map.js";
import "core-js/modules/es6.string.includes.js";
import "core-js/modules/es6.regexp.split.js";
import nwApiSDK from '@nerdwallet/nw-api-sdk';
import { ProviderAccountState } from "../constants/yodlee";
import { FINANCIAL_PORTAL_PRIMARY_SOURCES } from "../constants/account-aggregation";
import { getTransactionData } from "../selectors/yodlee";
/**
 * This file exposes utility functions for transforming FDP-based financial data objects
 * to the old Yodlee format. This is intended as a temporary measure to ease migration since
 * many existing components and logic expect data shaped in the Yodlee way. Long term, we
 * should refactor these components to use the schema/shape defined in query0.
 */

/**
 * Given an object and a mapping, returns a new object with the specified shape
 *
 * @param {Object} object - the object with data to map to the new shape
 * @param {Object} mapping - the mapping of target_field_name to source
 * @returns {Object} - the object in the new shape
 */

var mapFields = function mapFields(object, mapping) {
  return _reduce(mapping, function (res, v, k) {
    if (_isNil(object)) {
      return res;
    }

    if (typeof v === 'function') {
      _set(res, k, v(object));
    } else if (!_isNil(v) && _typeof(v) === 'object') {
      _set(res, k, mapFields(object, mapping[k]));
    } else {
      _set(res, k, _get(object, v, null));
    }

    return res;
  }, {});
}; // fdp balances are represented in cents


export var convertCentsAmountToDollarsAmount = function convertCentsAmountToDollarsAmount(amount) {
  return amount ? amount / 100 : 0;
};
var FINANCIAL_DATA_SUBTYPE_TO_YODLEE_BANK_ACCOUNT_TYPE = {
  // Bank
  'CHECKING:CHECKING_ACCOUNT': 'CHECKING',
  'CHECKING:PREPAID_CARD': 'PREPAID',
  'CHECKING:FSA': 'FSA',
  'CHECKING:COMMUTER': 'COMMUTER',
  'SAVINGS:SAVINGS_ACCOUNT': 'SAVINGS',
  'SAVINGS:MONEY_MARKET': 'MONEY_MARKET',
  'SAVINGS:CD': 'CD',
  'SAVINGS:OTHER_SAVINGS': 'OTHER',
  'RETIREMENT:IRA': 'IRA',
  // Bill
  'BILL:OTHER_BILL': 'BILLS',
  // Credit card
  'CREDIT_CARD:CREDIT_CARD': 'CREDIT',
  'CREDIT_CARD:STORE_CARD': 'STORE',
  'CREDIT_CARD:CHARGE_CARD': 'CHARGE',
  'CREDIT_CARD:OTHER_CARD': 'OTHER',
  // Insurance
  'INSURANCE:OTHER': '',
  // Retirement
  'RETIREMENT:401K': 'EMPLOYEE_RETIREMENT_ACCOUNT_401K',
  'RETIREMENT:401K_ROTH': 'EMPLOYEE_RETIREMENT_ACCOUNT_ROTH_401K',
  'RETIREMENT:403B': 'EMPLOYEE_RETIREMENT_SAVINGS_PLAN_403B',
  'RETIREMENT:403B_ROTH': 'ROTH_403B',
  'RETIREMENT:457_PLAN': 'DEFERRED_COMPENSATION_PLAN_457',
  'RETIREMENT:401A': 'MONEY_PURCHASE_PLAN',
  'RETIREMENT:OTHER_DEFINED_CONTRIBUTION_PLAN': 'DEFINED_CONTRIBUTION_PLAN',
  'RETIREMENT:PENSION_PLAN': 'PENSION_PLAN',
  'RETIREMENT:IRA_TRADITIONAL': 'INDIVIDUAL_RETIREMENT_ACCOUNT_IRA',
  'RETIREMENT:IRA_ROTH': 'ROTH_IRA',
  'RETIREMENT:IRA_SIMPLE': 'SIMPLE_IRA',
  'RETIREMENT:IRA_SEP': 'SEP_IRA',
  'RETIREMENT:CANADA_RETIREMENT_ACCOUNT': 'REGISTERED_RETIREMENT_SAVINGS_PLAN_RRSP',
  'RETIREMENT:OTHER_RETIREMENT': 'OTHER',
  // Investment
  'INVESTMENT:BROKERAGE': 'INDIVIDUAL',
  'INVESTMENT:EMPLOYEE_SAVINGS_PLAN': 'EMPLOYEE_SAVINGS_PLAN',
  'INVESTMENT:EMPLOYEE_STOCK_PLAN': 'RESTRICTED_STOCK_AWARD',
  'INVESTMENT:EMPLOYEE_STOCK_OPTION_PLAN': 'EMPLOYEE_STOCK_OPTION_PLAN',
  'INVESTMENT:PROFIT_SHARING_PLAN': 'PROFIT_SHARING_PLAN',
  'INVESTMENT:CORPORATE_INVESTMENT_ACCOUNT': 'CORPORATE_INVESTMENT_ACCOUNT',
  'INVESTMENT:ANNUITY': 'ANNUITY',
  'INVESTMENT:529_PLAN': 'EDUCATIONAL_SAVINGS_PLAN_529',
  'INVESTMENT:EDUCATION_SAVINGS_ACCOUNT': 'COVERDELL_EDUCATION_SAVINGS_ACCOUNT_ESA',
  'INVESTMENT:HSA': 'HEALTH_SAVINGS_ACCOUNT_HSA',
  'INVESTMENT:HRA': 'HEALTH_REIMBURSEMENT_ARRANGEMENT_HRA',
  'INVESTMENT:CASH_MANAGEMENT_ACCOUNT': 'CASH_MANAGEMENT_ACCOUNT',
  'INVESTMENT:TRUST': 'TRUST',
  'INVESTMENT:UTMA_UGMA_MINORS_ACCOUNT': 'UNIFORM_TRANSFER_TO_MINORS_ACT_UTMA',
  'INVESTMENT:CANADA_EDUCATION_SAVINGS_PLAN': 'REGISTERED_EDUCATION_SAVINGS_PLAN_RESP',
  'INVESTMENT:OTHER_INVESTMENT': '',
  // Loan
  'LOAN:STUDENT_LOAN': 'STUDENT_LOAN',
  'LOAN:AUTO_LOAN': 'AUTO_LOAN',
  'LOAN:MORTGAGE': 'MORTGAGE',
  'LOAN:PERSONAL_LOAN': 'PERSONAL_LOAN',
  'LOAN:LINE_OF_CREDIT': 'LINE_OF_CREDIT',
  'LOAN:INSTALLMENT_LOAN': 'INSTALLMENT_LOAN',
  'LOAN:HOME_LOAN': 'HOME_EQUITY_LINE_OF_CREDIT',
  'LOAN:OTHER_LOAN': 'OTHER',
  // Reward
  'REWARD:OTHER_REWARD': ''
};
var NW_TYPES = {
  CHECKING: 'CHECKING',
  SAVINGS: 'SAVINGS',
  INVESTMENT: 'INVESTMENT',
  RETIREMENT: 'RETIREMENT',
  OTHER: 'OTHER'
};
var FINANCIAL_ACCOUNT_TYPE_TO_NW_TYPE = {
  BANK: {
    CHECKING: NW_TYPES.CHECKING,
    PREPAID: NW_TYPES.CHECKING,
    CD: NW_TYPES.SAVINGS,
    FSA: NW_TYPES.SAVINGS,
    MONEY_MARKET: NW_TYPES.SAVINGS,
    OTHER: NW_TYPES.SAVINGS,
    SAVINGS: NW_TYPES.SAVINGS,
    '': NW_TYPES.SAVINGS,
    IRA: NW_TYPES.RETIREMENT,
    COMMUTER: NW_TYPES.OTHER
  },
  INVESTMENT: {
    HEALTH_REIMBURSEMENT_ARRANGEMENT_HRA: NW_TYPES.INVESTMENT,
    MONEY_MARKET_ACCOUNT: NW_TYPES.INVESTMENT,
    ADVISORY_ACCOUNT: NW_TYPES.INVESTMENT,
    ANNUITY: NW_TYPES.INVESTMENT,
    BROKERAGE_CASH: NW_TYPES.INVESTMENT,
    BROKERAGE_CASH_OPTION: NW_TYPES.INVESTMENT,
    BROKERAGE_LINK_ACCOUNT: NW_TYPES.INVESTMENT,
    BROKERAGE_MARGIN: NW_TYPES.INVESTMENT,
    CASH_MANAGEMENT_ACCOUNT: NW_TYPES.INVESTMENT,
    CHARITABLE_GIFT_ACCOUNT: NW_TYPES.INVESTMENT,
    CORPORATE_INVESTMENT_ACCOUNT: NW_TYPES.INVESTMENT,
    COVERDELL_EDUCATION_SAVINGS_ACCOUNT_ESA: NW_TYPES.INVESTMENT,
    CUSTODIAL_ACCOUNT: NW_TYPES.INVESTMENT,
    EDUCATIONAL_SAVINGS_PLAN_529: NW_TYPES.INVESTMENT,
    EMPLOYEE_BENEFIT_PLAN: NW_TYPES.INVESTMENT,
    EMPLOYEE_SAVINGS_PLAN: NW_TYPES.INVESTMENT,
    EMPLOYEE_STOCK_OPTION_PLAN: NW_TYPES.INVESTMENT,
    EMPLOYEE_STOCK_OWNERSHIP_PLAN_ESOP: NW_TYPES.INVESTMENT,
    EMPLOYEE_STOCK_PURCHASE_PLAN_ESPP: NW_TYPES.INVESTMENT,
    ESTATE: NW_TYPES.INVESTMENT,
    HEALTH_SAVINGS_ACCOUNT_HSA: NW_TYPES.INVESTMENT,
    INDEXED_ANNUITY: NW_TYPES.INVESTMENT,
    INDIVIDUAL: NW_TYPES.INVESTMENT,
    INVESTMENT_CLUB: NW_TYPES.INVESTMENT,
    IRREVOCABLE_TRUST: NW_TYPES.INVESTMENT,
    JOINT_TENANTS_COMMUNITY_PROPERTY: NW_TYPES.INVESTMENT,
    JOINT_TENANTS_TENANCY_IN_COMMON_JTIC: NW_TYPES.INVESTMENT,
    JOINT_TENANTS_TENANTS_BY_ENTIRETY: NW_TYPES.INVESTMENT,
    JOINT_TENANTS_WITH_RIGHTS_OF_SURVIVORSHI: NW_TYPES.INVESTMENT,
    LIFE_INCOME_FUND_LIF: NW_TYPES.INVESTMENT,
    LIVING_TRUST: NW_TYPES.INVESTMENT,
    NONQUALIFIED_DEFERRED_COMPENSATION_PLAN_: NW_TYPES.INVESTMENT,
    OFFSHORE_TRUST: NW_TYPES.INVESTMENT,
    PARTNERSHIP: NW_TYPES.INVESTMENT,
    PERFORMANCE_PLAN: NW_TYPES.INVESTMENT,
    PRECIOUS_METAL_ACCOUNT: NW_TYPES.INVESTMENT,
    PROFIT_SHARING_PLAN: NW_TYPES.INVESTMENT,
    PROPRIETORSHIP: NW_TYPES.INVESTMENT,
    REGISTERED_EDUCATION_SAVINGS_PLAN_RESP: NW_TYPES.INVESTMENT,
    RESTRICTED_STOCK_AWARD: NW_TYPES.INVESTMENT,
    REVOCABLE_TRUST: NW_TYPES.INVESTMENT,
    STOCK_BASKET_ACCOUNT: NW_TYPES.INVESTMENT,
    TRUST: NW_TYPES.INVESTMENT,
    UNIFORM_GIFT_TO_MINORS_ACT_UGMA: NW_TYPES.INVESTMENT,
    UNIFORM_TRANSFER_TO_MINORS_ACT_UTMA: NW_TYPES.INVESTMENT,
    VARIABLE_ANNUITY: NW_TYPES.INVESTMENT,
    '': NW_TYPES.INVESTMENT,
    CHURCH_ACCOUNT: NW_TYPES.RETIREMENT,
    DEFERRED_COMPENSATION_PLAN_457: NW_TYPES.RETIREMENT,
    DEFINED_BENEFIT_PLAN: NW_TYPES.RETIREMENT,
    DEFINED_CONTRIBUTION_PLAN: NW_TYPES.RETIREMENT,
    EMPLOYEE_RETIREMENT_ACCOUNT_401K: NW_TYPES.RETIREMENT,
    EMPLOYEE_RETIREMENT_ACCOUNT_ROTH_401K: NW_TYPES.RETIREMENT,
    EMPLOYEE_RETIREMENT_SAVINGS_PLAN_403B: NW_TYPES.RETIREMENT,
    EMPLOYEES_PENSION_SCHEME_EPS: NW_TYPES.RETIREMENT,
    EMPLOYEES_PROVIDENT_FUND_ORGANIZATION_EP: NW_TYPES.RETIREMENT,
    GROUP_RETIREMENT_SAVINGS_PLAN_GRSP: NW_TYPES.RETIREMENT,
    INDIVIDUAL_RETIREMENT_ACCOUNT_IRA: NW_TYPES.RETIREMENT,
    KEOGH_PLAN: NW_TYPES.RETIREMENT,
    MONEY_PURCHASE_PLAN: NW_TYPES.RETIREMENT,
    MONEY_PURCHASE_RETIREMENT_PLAN_401A: NW_TYPES.RETIREMENT,
    OTHER: NW_TYPES.RETIREMENT,
    PENSION_PLAN: NW_TYPES.RETIREMENT,
    REGISTERED_RETIREMENT_SAVINGS_PLAN_RRSP: NW_TYPES.RETIREMENT,
    ROLLOVER_IRA: NW_TYPES.RETIREMENT,
    ROTH_403B: NW_TYPES.RETIREMENT,
    ROTH_CONVERSION: NW_TYPES.RETIREMENT,
    ROTH_IRA: NW_TYPES.RETIREMENT,
    SARSEP_IRA: NW_TYPES.RETIREMENT,
    SEP_IRA: NW_TYPES.RETIREMENT,
    SIMPLE_IRA: NW_TYPES.RETIREMENT,
    SPOUSAL_RETIREMENT_SAVINGS_PLAN_SRSP: NW_TYPES.RETIREMENT,
    SUBSTANTIALLY_EQUAL_PERIODIC_PAYMENTS_SE: NW_TYPES.RETIREMENT,
    EXECUTOR: NW_TYPES.OTHER,
    NON_PROFIT_ORGANIZATION_501C: NW_TYPES.OTHER
  }
}; // Mapping of Yodlee 1.1 status messages to the legacy status codes as noted in this doc:
// https://docs.google.com/spreadsheets/d/10E2S_a26bpG-J2fVhbf2Wo2deF18B7MBmBl3IC_3PQ4/edit#gid=0

var FINANCIAL_PORTAL_ERROR_MESSAGES_TO_PROVIDER_ACCOUNT_STATUS_CODES = {
  DATA_RETRIEVAL_FAILED: 504,
  INSTITUTION_DOWN: 409,
  INSTITUTION_NOT_SUPPORTED: 507,
  INSTITUTION_REQUIRES_USER_ACTION: 429,
  INVALID_CREDENTIALS: 402,
  INVALID_MFA: 523,
  MFA_REQUIRED: 518,
  NO_DATA_OR_ACCOUNT: 414,
  OTHER_ERROR: 403,
  PORTAL_LOCKED: 407,
  PRODUCT_NOT_SUPPORTED: 575,
  LOGIN_REQUIRED: 526
};
var SPECIALIZED_ACCOUNT_TYPE_MAP = {
  BANK: 'bank_account_type',
  BILL: 'bill_account_type',
  CREDIT_CARD: 'credit_account_type',
  INVESTMENT: 'investment_account_type',
  LOAN: 'loan_account_type'
};

var getSpecializedAccountTypeForSubtype = function getSpecializedAccountTypeForSubtype(_ref) {
  var subtype = _ref.subtype,
      subtypeDetails = _ref.subtypeDetails;
  return FINANCIAL_DATA_SUBTYPE_TO_YODLEE_BANK_ACCOUNT_TYPE["".concat(subtype, ":").concat(subtypeDetails)];
};

var getSpecializedAccountTypesForAccount = function getSpecializedAccountTypesForAccount(account) {
  return _objectSpread(_objectSpread({}, Object.values(SPECIALIZED_ACCOUNT_TYPE_MAP).reduce(function (acc, specializedAccountType) {
    return _objectSpread(_objectSpread({}, acc), {}, _defineProperty({}, specializedAccountType, null));
  }, {})), {}, _defineProperty({}, SPECIALIZED_ACCOUNT_TYPE_MAP[account.type], getSpecializedAccountTypeForSubtype(account)));
};

var FINANCIAL_ACCOUNT_TO_YODLEE_ACCOUNT_FIELD_MAP = {
  account_name: 'name',
  // display on accounts tab
  account_number_last_4: 'mask',
  // display on accounts tab
  account_status: 'status',
  annual_percentage_yield: null,
  available_balance: null,
  balance: function balance(acct) {
    return convertCentsAmountToDollarsAmount(acct.balance);
  },
  created_at: 'createdAt',
  currency: 'currency',
  details_last_synced_at: 'updatedAt',
  extra_data: 'extra',
  id: 'id',
  interest_earned_ytd: null,
  interest_rate: null,
  is_active_sync: function is_active_sync(account) {
    return _get(account, 'financialPortal.accountsState') === 'RETRIEVAL_IN_PROGRESS';
  },
  is_non_usd: function is_non_usd(acct) {
    return acct.currency !== 'USD';
  },
  last_refreshed: 'lastSyncedAt',
  // used for display on accts tab
  last_synced_at: 'lastSyncedAt',
  nw_enabled: function nw_enabled(acct) {
    return !acct.isHidden;
  },
  // used to toggle disabled state
  nw_type: function nw_type(acct) {
    return _get(FINANCIAL_ACCOUNT_TYPE_TO_NW_TYPE, "".concat(acct.type, ".").concat(getSpecializedAccountTypeForSubtype(acct)));
  },
  provider_account_state_id: 'financialPortal.id',
  // shimming portal id in here since it's used for mutations
  provider_name: 'financialPortal.marketplaceInstitution.name',
  refresh_status_code: null,
  refresh_status_message: null,
  // While there is a `transactionsSyncedAt` on FDP portals, it doesn't have the semantics we expect.
  // Since the only known use case for consuming this field is checking whether an account is syncing for the first time,
  // we can instead map this back to the portal's accounts state
  transactions_last_synced_at: function transactions_last_synced_at(account) {
    return _get(account, 'financialPortal.isActiveSync') || ['RETRIEVAL_IN_PROGRESS', 'PARTIAL_DATA_RETRIEVED'].includes(_get(account, 'financialPortal.transactionsState')) ? null : // explicitly fall back to `true` so truthy checks pass
    _get(account, 'transactionsSyncedAt') || true;
  },
  yodlee_account_type: function yodlee_account_type(acct) {
    return _camelCase(acct.type);
  } // used to determine where to link from accounts tab, which icon to show on accounts tab

};
export var convertFinancialAccountToYodleeAccount = function convertFinancialAccountToYodleeAccount(financialAccount) {
  return _objectSpread(_objectSpread({}, mapFields(financialAccount, FINANCIAL_ACCOUNT_TO_YODLEE_ACCOUNT_FIELD_MAP)), getSpecializedAccountTypesForAccount(financialAccount));
};
var portalAuthStateToProviderAccountState = {
  INIT: ProviderAccountState.LOGIN_IN_PROGRESS,
  ERROR: ProviderAccountState.ERROR,
  AUTHENTICATED: ProviderAccountState.ACTIVE,
  USER_ACTION_REQUIRED: ProviderAccountState.MFA_REQUIRED
}; // https://github.com/NerdWallet/identity.nerdwallet/blob/a3f66e9d054f7c138ea78b0214bfdd84ddfb50e0/src/identity/models/provider_account_state.py#L17
// Also see field mappings here: https://nerdwallet.atlassian.net/wiki/spaces/ENG/pages/552108098/FDP+-+Legacy+Data+Migration#FDP-LegacyDataMigration-fdp_auth_state_from_identity

var getProviderAccountStateStateFromPortal = function getProviderAccountStateStateFromPortal(portal) {
  var _portal$migrationDeta;

  if (portal !== null && portal !== void 0 && portal.isActiveSync || portal !== null && portal !== void 0 && (_portal$migrationDeta = portal.migrationDetails) !== null && _portal$migrationDeta !== void 0 && _portal$migrationDeta.hasUserConfirmed && (portal === null || portal === void 0 ? void 0 : portal.primarySource) === FINANCIAL_PORTAL_PRIMARY_SOURCES.YODLEE) {
    return ProviderAccountState.LOGIN_IN_PROGRESS;
  }
  /**
   * pas.state => portal.authState => pas.state loses granularity for provider accounts in ERROR, but only portals
   * whose associated pas.state is in ERROR will have a truthy error message field
   */


  if ((portal === null || portal === void 0 ? void 0 : portal.primarySource) === FINANCIAL_PORTAL_PRIMARY_SOURCES.YODLEE && portal !== null && portal !== void 0 && portal.errorMessage) {
    return ProviderAccountState.ERROR;
  } // Plaid portals in ERROR and MFA_REQ both get sent to Plaid Link, but client messaging is better for the ERROR state


  if ((portal === null || portal === void 0 ? void 0 : portal.primarySource) === FINANCIAL_PORTAL_PRIMARY_SOURCES.PLAID && (portal === null || portal === void 0 ? void 0 : portal.authState) === 'USER_ACTION_REQUIRED') {
    return ProviderAccountState.ERROR;
  }

  return portalAuthStateToProviderAccountState[portal === null || portal === void 0 ? void 0 : portal.authState];
};

export var FINANCIAL_PORTAL_PRODUCT_STATUSES = {
  PARTIAL_DATA_RETRIEVED: 'PARTIAL_DATA_RETRIEVED',
  DATA_RETRIEVED: 'DATA_RETRIEVED',
  RETRIEVAL_FAILED: 'RETRIEVAL_FAILED',
  RETRIEVAL_IN_PROGRESS: 'RETRIEVAL_IN_PROGRESS'
};
var ERROR_PRIORITIES = {
  HIGH: 'HIGH_PRIORITY',
  NORMAL: 'NORMAL_PRIORITY',
  MUTED: 'MUTED_ERROR'
};

var getProviderIdFromPortal = function getProviderIdFromPortal(portal) {
  var _portal$marketplaceIn2;

  var primarySource = portal === null || portal === void 0 ? void 0 : portal.primarySource;

  if (primarySource === FINANCIAL_PORTAL_PRIMARY_SOURCES.YODLEE) {
    var _portal$marketplaceIn;

    var externalId = (_portal$marketplaceIn = portal.marketplaceInstitution) === null || _portal$marketplaceIn === void 0 ? void 0 : _portal$marketplaceIn.externalId;
    return externalId ? Number(externalId) : null;
  }

  return portal === null || portal === void 0 ? void 0 : (_portal$marketplaceIn2 = portal.marketplaceInstitution) === null || _portal$marketplaceIn2 === void 0 ? void 0 : _portal$marketplaceIn2.id;
};

var getIavSupportedAccountsFromPortal = function getIavSupportedAccountsFromPortal(portal) {
  var accounts = _get(portal, 'accounts', []);

  return accounts.filter(function (account) {
    return account.type === 'BANK' && (account.subtype === 'SAVINGS' && account.subtypeDetails === 'SAVINGS_ACCOUNT' || account.subtype === 'CHECKING');
  });
};

var FINANCIAL_PORTAL_TO_YODLEE_PROVIDER_ACCOUNT_MAP = {
  accounts: function accounts(portal) {
    return _get(portal, 'accounts', []).map(function (financialAccount) {
      return convertFinancialAccountToYodleeAccount(financialAccount);
    });
  },
  provider_account_state: {
    acct_profile_status: null,
    aggregation_status: null,
    // NEW fields only for FDP portals
    alert_message: 'alertMessage',
    alert_name: 'alertName',
    alert_priority: 'alertPriority',
    authenticated_at: 'createdAt',
    auto_refresh_required: function auto_refresh_required(portal) {
      return _some(_get(portal, 'accounts', []), function (account) {
        return account.autoRefreshRequired;
      });
    },
    // TODO debug this, it says it should have it on portal.
    error_priority: function error_priority(portal) {
      return ERROR_PRIORITIES[portal.errorPriority] || '';
    },
    // NEW field only for FDP portals
    error_message: 'errorMessage',
    error_resolutions: {
      primary_action: 'primaryClientAction',
      secondary_action: 'secondaryClientAction'
    },
    iav_verified: function iav_verified(portal) {
      var iavSupportedAccounts = getIavSupportedAccountsFromPortal(portal);
      return iavSupportedAccounts.length > 0 && _every(iavSupportedAccounts, function (account) {
        return account.iavVerified;
      });
    },
    id: 'id',
    // used for refresh functionality, show/hide functionality
    identity_id: 'identityId',
    // TODO: Where should constants like this live? Should the schema constants like this be available somehow?
    is_active_sync: 'isActiveSync',
    is_auto_refreshing: 'isAutoRefreshing',
    // used by accts tab to determine if refreshing, but may roll up to is_refreshing
    is_balance_refreshing: 'isBalanceRefreshing',
    is_refreshable: 'isRefreshable',
    is_refreshing: function is_refreshing(portal) {
      return !_get(portal, 'isActiveSync') && (_get(portal, 'isManualRefreshing') || _get(portal, 'isBalanceRefreshing') || _get(portal, 'isAutoRefreshing'));
    },
    last_refresh_attempt: 'lastRefreshedAt',
    mfa_form: null,
    previous_state: null,
    primary_source: 'primarySource',
    provider: {
      favicon: 'marketplaceInstitution.logoUrl',
      // used for display
      iav_admin_deactivated: 'marketplaceInstitution.iavAdminDeactivated',
      iav_enabled: function iav_enabled(portal) {
        var _portal$marketplaceIn3;

        var iavSupportedAccounts = getIavSupportedAccountsFromPortal(portal);
        return (// If the portal has finished syncing accounts and has none, then we return it as not IAV enabled so that we don't
          // try to IAV verify a portal with no accounts.
          (iavSupportedAccounts.length > 0 || portal.isActiveSync) && (portal === null || portal === void 0 ? void 0 : (_portal$marketplaceIn3 = portal.marketplaceInstitution) === null || _portal$marketplaceIn3 === void 0 ? void 0 : _portal$marketplaceIn3.iavEnabled)
        );
      },
      is_deprecated: function is_deprecated(portal) {
        return _get(portal, 'marketplaceInstitution.displayStatus') === 'INACTIVE';
      },
      id: getProviderIdFromPortal,
      // Additional field used to provide the FDP entity ID for the marketplace institution mapped to the Yodlee provider shape.
      marketplaceInstitutionId: 'marketplaceInstitution.id',
      login_form: null,
      login_url: 'marketplaceInstitution.loginUrl',
      logo: 'marketplaceInstitution.logoUrl',
      name: 'marketplaceInstitution.name',
      // used for display on accts tab
      provider_details: null,
      security_requirements: null
    },
    provider_account_id: function provider_account_id(_ref2) {
      var sourceEntityId = _ref2.sourceEntityId;
      return sourceEntityId ? Number(sourceEntityId) : null;
    },
    // used for fix link flow
    provider_id: getProviderIdFromPortal,
    provider_name: 'marketplaceInstitution.name',
    state: getProviderAccountStateStateFromPortal,
    state_set_at: 'authStateSetAt',
    status_code: function status_code(portal) {
      var statusCode = FINANCIAL_PORTAL_ERROR_MESSAGES_TO_PROVIDER_ACCOUNT_STATUS_CODES[portal.errorMessage];
      return statusCode ? String(statusCode) : null;
    },
    status_message: null,
    sum_assets: null,
    sum_bank_balances: null,
    sum_debts: null,
    sum_investment_balances: null,
    sum_loan_balances: null
  },
  sum_assets: null,
  sum_bank_balances: null,
  sum_debts: null,
  sum_investment_balances: null,
  sum_loan_balances: null
};
export var convertPortalToProviderAccount = function convertPortalToProviderAccount(portal, accounts) {
  return mapFields(_objectSpread(_objectSpread({}, portal), {}, {
    accounts: accounts
  }), FINANCIAL_PORTAL_TO_YODLEE_PROVIDER_ACCOUNT_MAP);
};
var updateFinancialAccountVisibilityToYodleeAccountVisibility = {
  financialAccountId: 'id',
  isHidden: function isHidden(_ref3) {
    var nwEnabled = _ref3.nwEnabled;
    return !nwEnabled;
  }
};
var YODLEE_REFRESH_PARAMS_TO_FINANCIAL_PORTAL_PARAMS_MAP = {
  financialPortalId: 'providerAccountStateId',
  isAutoRefresh: 'isAutoRefresh'
};
export var convertYodleeRefreshParamsToFinancialPortalParams = function convertYodleeRefreshParamsToFinancialPortalParams(params) {
  return mapFields(params, YODLEE_REFRESH_PARAMS_TO_FINANCIAL_PORTAL_PARAMS_MAP);
};
export var convertFinancialAccountVisibilityToYodleeAccountVisibility = function convertFinancialAccountVisibilityToYodleeAccountVisibility(input) {
  return mapFields(input, updateFinancialAccountVisibilityToYodleeAccountVisibility);
};
var TRANSACTION_V1_CATEGORIES = {
  ADVERTISING: 100,
  ATM_CASH_WITHDRAWALS: 25,
  AUTOMOTIVE_EXPENSES: 2,
  BUSINESS_MISCELLANEOUS: 102,
  CABLE_SATELLITE_SERVICES: 15,
  CONSULTING: 92,
  CHARITABLE_GIVING: 3,
  CHECKS: 33,
  CHILD_DEPENDENT_EXPENSES: 4,
  CLOTHING_SHOES: 5,
  CREDIT_CARD_PAYMENTS: 26,
  DEPOSITS: 27,
  DUES_AND_SUBSCRIPTIONS: 108,
  EDUCATION: 6,
  ELECTRONICS: 43,
  ENTERTAINMENT: 7,
  EXPENSE_REIMBURSEMENT: 114,
  GASOLINE_FUEL: 8,
  GENERAL_MERCHANDISE: 44,
  GIFTS: 9,
  GROCERIES: 10,
  HEALTHCARE_MEDICAL: 11,
  HOBBIES: 34,
  HOME_IMPROVEMENT: 13,
  HOME_MAINTENANCE: 12,
  INSURANCE: 14,
  INTEREST: 96,
  INVESTMENT_INCOME: 30,
  LOANS: 17,
  OFFICE_MAINTENANCE: 110,
  OFFICE_SUPPLIES: 45,
  ONLINE_SERVICES: 16,
  OTHER_BILLS: 35,
  OTHER_EXPENSES: 19,
  OTHER_INCOME: 32,
  MORTGAGES: 18,
  PAYCHECKS_SALARY: 29,
  PERSONAL_CARE: 20,
  PETS_PET_CARE: 42,
  POSTAGE_AND_SHIPPING: 104,
  PRINTINGS: 106,
  REFUNDS_ADJUSTMENTS: 227,
  RENT: 21,
  RESTAURANTS_DINING: 22,
  RETIREMENT_CONTRIBUTIONS: 41,
  RETIREMENT_INCOME: 31,
  REWARDS: 225,
  SALES: 94,
  SAVINGS: 40,
  SECURITIES_TRADES: 36,
  SERVICE_CHARGES_AND_FEES: 24,
  SERVICES: 98,
  TAXES: 37,
  TELEPHONE_SERVICES: 38,
  TRANSFERS: 28,
  TRAVEL: 23,
  UTILITIES: 39,
  WAGES_PAID: 112
};
var TRANSACTION_V2_CATEGORIES = {
  ENTERTAINMENT: 'ENTERTAINMENT',
  FEES: 'FEES',
  GROCERIES: 'GROCERIES',
  HEALTH: 'HEALTH',
  HOUSING: 'HOUSING',
  INCOME: 'INCOME',
  OTHER: 'OTHER',
  RESTAURANTS: 'RESTAURANTS',
  SHOPPING: 'SHOPPING',
  TRANSFERS: 'TRANSFERS',
  TRANSPORT: 'TRANSPORT',
  TRAVEL: 'TRAVEL',
  UTILITIES: 'UTILITIES'
};
var TRANSACTION_TAGS = {
  ACCOUNT_FEE: 'ACCOUNT_FEE',
  BANK_FEE_OVERDRAFT: 'BANK_FEE_OVERDRAFT',
  BANK_FEE_ATM: 'BANK_FEE_ATM',
  REFUND: 'REFUND',
  ACCOUNT_FEE_ANNUAL: 'ACCOUNT_FEE_ANNUAL',
  EDUCATION: 'EDUCATION',
  CASH_WITHDRAWAL: 'CASH_WITHDRAWAL',
  CHECK_PAYMENT: 'CHECK_PAYMENT',
  RENT_PAYMENT: 'RENT_PAYMENT',
  INVESTMENT_INCOME: 'INVESTMENT_INCOME',
  TAX_REFUND: 'TAX_REFUND',
  DEPOSIT: 'DEPOSIT',
  PAYROLL_DEPOSIT: 'PAYROLL_DEPOSIT',
  REBALANCE_TRANSFER: 'REBALANCE_TRANSFER'
};
var FINANCIAL_DATA_TRANSACTION_TO_YODLEE_TRANSACTION_MAP = {
  account_id: function account_id(transaction) {
    return _get(transaction, 'account.id');
  },
  amount: function amount(transaction) {
    return convertCentsAmountToDollarsAmount(transaction.amount);
  },
  base_type: 'type',
  canonical_merchant_id: null,
  canonical_merchant_name: 'merchantName',
  category: 'category',
  category_override: null,
  category_type: function category_type(_ref4) {
    var category = _ref4.category,
        type = _ref4.type,
        _ref4$tags = _ref4.tags,
        tags = _ref4$tags === void 0 ? [] : _ref4$tags;

    if (type === 'CREDIT' && category === TRANSACTION_V2_CATEGORIES.INCOME && _intersection(tags, [TRANSACTION_TAGS.REFUND, TRANSACTION_TAGS.INVESTMENT_INCOME]).length === 0) {
      return 'INCOME';
    }

    if (type === 'DEBIT' && !tags.includes(TRANSACTION_TAGS.BANK_FEE_OVERDRAFT) && ![TRANSACTION_V2_CATEGORIES.TRANSFERS, TRANSACTION_V2_CATEGORIES.INCOME, TRANSACTION_V2_CATEGORIES.FEES, TRANSACTION_V2_CATEGORIES.HOUSING, TRANSACTION_V2_CATEGORIES.UTILITIES].includes(category) && _intersection(tags, [TRANSACTION_TAGS.CASH_WITHDRAWAL, TRANSACTION_TAGS.CHECK_PAYMENT, TRANSACTION_TAGS.RENT_PAYMENT]).length === 0) {
      return 'EXPENSE';
    }
  },
  // strip off TZ info to match Yodlee API so things get bucketed & sorted properly
  date: function date(t) {
    return _get(t, 'date', '').split('T')[0];
  },
  description: 'description',
  description_consumer: null,
  description_original: null,
  id: 'id',
  interest: null,
  merchant_category_override: null,
  merchant_id: null,
  merchant_name: 'merchantName',
  merchant_name_ds: null,
  posted_date: null,
  posted_order: null,
  running_balance: null,
  status: function status(_ref5) {
    var isPending = _ref5.isPending;
    return isPending ? 'PENDING' : 'POSTED';
  },
  subtype: function subtype(_ref6) {
    var category = _ref6.category,
        type = _ref6.type,
        _ref6$tags = _ref6.tags,
        tags = _ref6$tags === void 0 ? [] : _ref6$tags;

    if (type === 'CREDIT' && category === TRANSACTION_V2_CATEGORIES.INCOME && _intersection(tags, [TRANSACTION_TAGS.REFUND, TRANSACTION_TAGS.INVESTMENT_INCOME]).length === 0) {
      return 'DEPOSIT';
    }

    if (type === 'DEBIT' && tags.includes(TRANSACTION_TAGS.BANK_FEE_OVERDRAFT)) {
      return 'NSF_FEES';
    }

    if (tags.includes(TRANSACTION_TAGS.ACCOUNT_FEE)) {
      return 'ANNUAL_FEE';
    }
  },
  tags: function tags(_ref7) {
    var isHidden = _ref7.isHidden;
    return isHidden ? [nwApiSDK.IDENTITY_TRANSACTION_TAGS.IGNORED] : [];
  },
  transaction_date: null,
  type: 'type',
  yodlee_account_type: null,
  // mapped in backend, but only used in updated_transactions_visibility legacy event on web
  yodlee_category: null,
  // mapped in backend, but not used
  yodlee_category_id: function yodlee_category_id(_ref8) {
    var category = _ref8.category,
        type = _ref8.type,
        _ref8$tags = _ref8.tags,
        tags = _ref8$tags === void 0 ? [] : _ref8$tags;

    if (type === 'DEBIT' && !tags.includes(TRANSACTION_TAGS.BANK_FEE_OVERDRAFT) && ![TRANSACTION_V2_CATEGORIES.TRANSFERS, TRANSACTION_V2_CATEGORIES.INCOME, TRANSACTION_V2_CATEGORIES.FEES, TRANSACTION_V2_CATEGORIES.HOUSING, TRANSACTION_V2_CATEGORIES.UTILITIES].includes(category) && _intersection(tags, [TRANSACTION_TAGS.CASH_WITHDRAWAL, TRANSACTION_TAGS.CHECK_PAYMENT, TRANSACTION_TAGS.RENT_PAYMENT]).length === 0) {
      return TRANSACTION_V1_CATEGORIES.OTHER_EXPENSES;
    }

    if (tags.includes(TRANSACTION_TAGS.DEPOSIT)) {
      return TRANSACTION_V1_CATEGORIES.DEPOSITS;
    }

    if (tags.includes(TRANSACTION_TAGS.PAYROLL_DEPOSIT)) {
      return TRANSACTION_V1_CATEGORIES.RETIREMENT_CONTRIBUTIONS;
    }

    if (tags.includes(TRANSACTION_TAGS.REBALANCE_TRANSFER)) {
      return TRANSACTION_V1_CATEGORIES.TRANSFERS;
    }

    if (tags.includes(TRANSACTION_TAGS.REFUND)) {
      return TRANSACTION_V1_CATEGORIES.REFUNDS_ADJUSTMENTS;
    }

    if (tags.includes(TRANSACTION_TAGS.CHECK_PAYMENT)) {
      return TRANSACTION_V1_CATEGORIES.CHECKS;
    }

    if (tags.includes(TRANSACTION_TAGS.ACCOUNT_FEE_ANNUAL)) {
      return TRANSACTION_V1_CATEGORIES.SERVICE_CHARGES_AND_FEES;
    }
  },
  yodlee_id: 'sourceEntityId'
};
export var convertYodleeTransactionParamsToFinancialDataParams = function convertYodleeTransactionParamsToFinancialDataParams(params, state) {
  var _params$limit = params.limit,
      limit = _params$limit === void 0 ? 50 : _params$limit,
      _params$filter = params.filter,
      filter = _params$filter === void 0 ? {} : _params$filter,
      offset = params.offset,
      _params$includeDisabl = params.includeDisabledAccounts,
      includeDisabledAccounts = _params$includeDisabl === void 0 ? !!_get(params, 'filter.accountId') : _params$includeDisabl,
      resultSetToken = params.resultsSetToken;
  var accountId = filter.accountId,
      merchantName = filter.merchantName,
      category = filter.category,
      tag = filter.tag;

  var numFetchedTransactions = _size(_get(getTransactionData(state.identity, filter), 'transactions.length'));

  var fdpParams = {
    accountId: accountId,
    merchantName: merchantName,
    category: category,
    includeHiddenAccounts: includeDisabledAccounts,
    pagination: {
      limit: limit,
      offset: offset !== undefined ? offset : numFetchedTransactions,
      resultSetToken: resultSetToken
    }
  };

  if (tag === nwApiSDK.IDENTITY_TRANSACTION_TAGS.IGNORED) {
    fdpParams.isHidden = true;
  }

  return fdpParams;
};
var YODLE_MERCHANT_CATEGORY_PARAMS_TO_FINANCIAL_DATA_PARAMS_MAP = {
  transactionId: 'transactionId',
  category: 'category',
  merchantName: 'canonicalMerchantName'
};
export var convertYodleeMerchantCategoryParamsToFinancialDataParams = function convertYodleeMerchantCategoryParamsToFinancialDataParams(params) {
  return mapFields(params, YODLE_MERCHANT_CATEGORY_PARAMS_TO_FINANCIAL_DATA_PARAMS_MAP);
};
export var convertFinancialDataTransactionToYodleeTransaction = function convertFinancialDataTransactionToYodleeTransaction(transaction) {
  return mapFields(transaction, FINANCIAL_DATA_TRANSACTION_TO_YODLEE_TRANSACTION_MAP);
};
export var convertFinancialDataTransactionsToYodleeTransactions = function convertFinancialDataTransactionsToYodleeTransactions(transactions) {
  return transactions.map(convertFinancialDataTransactionToYodleeTransaction);
};